import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import OutlineButton from 'components/OutlineButton';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import Table from 'components/Table';

import { IOperator } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { USER_LEVELS } from 'utils/constants';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import OperatorModalContent from './ActionModalContent';

export default function OperatorsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState('');
  const [operators, setOperators] = useState<Array<IOperator>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);

  const toggleOperator = async (userId: string, isActive: boolean) => {
    setLoadingId(userId);
    let result: any = await generalApi.post('/toggle_user', {
      userId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(isActive ? 'Operador deshabilitado' : 'Operador habilitado');
    setRefresh(true);
  };

  useEffect(() => {
    const getOperators = async () => {
      setLoading(true);
      const result = await generalApi.get('/operators');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperators(result.data);
      setLoading(false);
    };
    if (refresh) {
      getOperators();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal isOpen={isActionOpen} onClose={() => setIsActionOpen(false)}>
          <OperatorModalContent
            title="Crear operador"
            onClose={() => setIsActionOpen(false)}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
            }}
          />
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Operadores</p>
            <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
              <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                Nuevo Operador
              </OutlineButton>
            </div>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando operadores"
            columns={[
              {
                title: 'Tipo',
                dataKey: 'level',
                render: (item: IOperator) => USER_LEVELS[item.level],
              },
              {
                title: 'Nombres',
                dataKey: 'name',
              },
              {
                title: 'Apellidos',
                dataKey: 'lname_p',
                render: (item: IOperator) => `${item.lname_p} ${item.lname_m}`,
              },
              {
                title: 'Correo',
                dataKey: 'email',
              },
              {
                title: 'Opciones',
                dataKey: 'options',
                disableSort: true,
                render: (item: IOperator) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      className="w-auto"
                      isLoading={loadingId === item._id}
                      color={item.is_active ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE}
                      onClick={() => toggleOperator(item._id, item.is_active)}
                    >
                      {item.is_active ? 'Deshabilitar' : 'Habilitar'}
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={operators}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
